<script setup lang="ts">
import { computed } from 'vue';
import { useRoute } from 'vue-router';

import AmcLogo from '../../../../svg/amc-logo-horizontal.svg';
import { alerts, createAlert } from '../../alerts.ts';
import PublicPageLayout from '../../components/PublicPageLayout.vue';
import RequestStatus from '../../components/RequestStatus.vue';
import { useRequestBase } from '../../composables/request.ts';
import { usePublicFlowAdapter } from '../adapter.ts';
import ActorIdentity from './ActorIdentity.vue';
import FlowAuthentication from './FlowAuthentication.vue';

type CouponDetails = {
    months: number | null;
    redeemedByEmail: string | null;
    product: {
        name: string;
        slug: string;
        description: string | null;
        color: string;
        iconHtml: string | null;
    };
};

type CouponContext =
    | { status: 'invalid' }
    | (CouponDetails & {
          status: 'available' | 'redeemed' | 'alreadyRedeemed' | 'expired';
      });

type Redemption = { redirect: string; message: string };

const route = useRoute();
const adapter = usePublicFlowAdapter();
const sourceUrl = computed(() => new URL(route.fullPath, adapter.baseUrl).href);
const endpoint = computed(
    () => `${adapter.apiUrl}/coupon-redemptions/${String(route.params.coupon)}`,
);
const contextRequest = useRequestBase<CouponContext>(
    () => `${endpoint.value}?${new URLSearchParams({ url: sourceUrl.value })}`,
    { refetch: true },
).json();
const context = contextRequest.data;
const redeemRequest = useRequestBase<Redemption>(endpoint, { immediate: false })
    .json()
    .post(computed(() => ({ url: sourceUrl.value })));

async function navigate(path: string) {
    try {
        await adapter.refreshAuthenticatedState?.();
    } catch {
        return;
    }

    await adapter.navigate(path, 'next');
}

contextRequest.onFetchResponse((response) => {
    if (response.ok && context.value?.status === 'redeemed') {
        void navigate(`/${context.value.product.slug}`);
    }
});

redeemRequest.onFetchResponse((response) => {
    const redemption = redeemRequest.data.value;

    if (!response.ok || !redemption) {
        return;
    }

    alerts.show(createAlert({ type: 'success', message: redemption.message }));
    void navigate(redemption.redirect);
});
</script>

<template>
    <RequestStatus :request="contextRequest">
        <PublicPageLayout
            v-if="context?.status === 'invalid'"
            :brand-url="adapter.brandUrl"
            title="Link Invalid"
            description="This link is invalid. Please contact support@emedici.com for help."
        />

        <PublicPageLayout
            v-else-if="context?.status === 'expired'"
            :brand-url="adapter.brandUrl"
            title="Coupon Expired"
        >
            <template #description>
                <p>This coupon has expired and can no longer be redeemed.</p>
                <button class="btn btn--lg bg-primary full-width" @click="adapter.back('/')">
                    Continue to eMedici
                </button>
            </template>
        </PublicPageLayout>

        <PublicPageLayout
            v-else-if="context?.status === 'alreadyRedeemed'"
            :brand-url="adapter.brandUrl"
            title="Coupon Already Used"
        >
            <template #description>
                <p>
                    <template v-if="context.redeemedByEmail">
                        This coupon has already been redeemed by
                        <strong>{{ context.redeemedByEmail }}.</strong>
                        If this looks like yours, sign in with that email address.
                    </template>
                    <template v-else>
                        This coupon has already been redeemed under an account that is no longer
                        active. Please <a href="mailto:support@emedici.com">contact support</a> and
                        include the reference number {{ route.params.coupon }}.
                    </template>
                </p>
            </template>
        </PublicPageLayout>

        <PublicPageLayout
            v-else-if="context?.status === 'available'"
            :brand-url="adapter.brandUrl"
            :product="context.product.slug.startsWith('amc') ? undefined : context.product"
        >
            <template v-if="context.product.slug.startsWith('amc')" #branding>
                <AmcLogo class="coupon-redemption__amc-logo align-self-start" style="width: 80%" />
            </template>

            <template #title>
                Redeem Your Free
                <span v-if="context.months" class="nowrap"> {{ context.months }}-Month </span>
                Access
            </template>

            <ActorIdentity v-if="adapter.actorEmail.value" />
            <FlowAuthentication v-else :intended="route.fullPath" />

            <template v-if="adapter.actorEmail.value" #footer>
                <button
                    type="button"
                    class="btn bg-primary btn--lg rounded-full full-width"
                    :class="{ 'is-loading': redeemRequest.isFetching.value }"
                    :disabled="redeemRequest.isFetching.value"
                    @click="redeemRequest.execute()"
                >
                    Redeem Access
                </button>
            </template>
        </PublicPageLayout>
    </RequestStatus>
</template>

<style scoped lang="less">
@media @query-tablet-down {
    .coupon-redemption__amc-logo {
        align-self: center;
    }
}
</style>
